import styled from '@emotion/styled';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useEventLogging } from '@innovamat/event-logging';
import {
  APP_PATHS,
  useNavigation,
  useOrganization,
  useUser,
} from '@innovamat/ga-features';
import {
  AnnouncementPanel,
  Button,
  useGlimmerTheme,
} from '@innovamat/glimmer-components';
import { CLASSROOM_TYPE, sortClassrooms } from '@innovamat/radiance-utils';

import {
  UpdatePeriodAnnouncementPanel,
  useCheckIsUpdatePeriod,
} from '../../../components/update-period-announcement-panel';
import { useClassrooms } from '../../hooks/use-classrooms';
import { useManageClassrooms } from '../../hooks/use-manage-classrooms';
import { AddClassroomModal } from '../add-classroom-modal';
import { ConfirmationModal } from '../confirmation-modal';

import { useSaveTeacherAccess } from '../../../user-management/hooks';
import { useClassesDisplay } from './hooks/use-classes-display';
import { MyClassesClassCard } from './my-classes-class-card';
import { MyClassesHeader } from './my-classes-header';
import { MyClassesSkeleton } from './my-classes-skeleton';
import { NoClassesEmptyState } from './no-classes-empty-state';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ClassesContainer = styled.div<{
  isExtendable: boolean;
}>`
  ${({ isExtendable }) => (isExtendable ? 'padding-bottom: 1rem;' : '')}
  border-radius: 1rem;
  background-color: ${({ theme }) =>
    theme.tokens.color.alias.cm.surface['surface-secondary'].value};
  border: 1px solid
    ${({ theme }) => theme.tokens.color.alias.cm.border['border-subtle'].value};
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  gap: 0.625rem;
  min-height: 10rem;
`;

const ClassesGrid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 1rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const ClassesWrapper = styled.div<{ noClasses: boolean; maxHeight: number }>`
  padding: ${({ noClasses }) => (noClasses ? '0' : '1rem')};
  max-height: ${({ maxHeight }) => (maxHeight ? `${maxHeight}px` : '158px')};
  min-height: 126px;
  overflow: hidden;
  width: 100%;

  transition: max-height 0.3s ease-in-out;
`;

export function MyClasses(): JSX.Element {
  const { goToClassroom } = useNavigation();
  const { setEventData } = useEventLogging();
  const { t } = useTranslation();
  const { user } = useUser();
  const isUpdatePeriod = useCheckIsUpdatePeriod();
  const navigate = useNavigate();
  const theme = useGlimmerTheme();
  const [isExpanded, setIsExpanded] = useState(false);
  const [maxHeight, setMaxHeight] = useState(0);
  const { onSaveTeacherAccess } = useSaveTeacherAccess();

  const organizationId = user?.organizationId ?? '';

  const {
    confirmModalData,
    editModalData,
    handleDeleteClassWithConfirmation,
    handleLeaveClassWithConfirmation,
    handleOnError,
    openEditModal,
  } = useManageClassrooms();

  const { isJourneysEnabled, loading: isLoadingOrganization } =
    useOrganization();

  const classroomType = isJourneysEnabled
    ? CLASSROOM_TYPE.JOURNEYS
    : CLASSROOM_TYPE.CLASSROOM;

  const { classrooms, isError, isSuccess } = useClassrooms({
    mine: true,
    organizationId: organizationId,
    type: classroomType,
    enabled: !isLoadingOrganization,
  });

  const sortedClassrooms = useMemo(() => {
    return sortClassrooms(classrooms || []);
  }, [classrooms]);

  const { noClasses, isExtendable } = useClassesDisplay({
    classrooms: sortedClassrooms || [],
    theme,
  });

  const goToJoinClassroom = (): void =>
    navigate(APP_PATHS.JOIN_TO_CLASSROOM_URL);

  const goToJoinJourneys = (): void => navigate(APP_PATHS.JOIN_TO_JOURNEYS_URL);

  const goToJoinClassroomFirstTime = (): void =>
    navigate(APP_PATHS.JOIN_TO_CLASSROOM_FT_URL);

  const goToJoinClassroomsButton = (
    <Button
      onClick={() =>
        isJourneysEnabled ? goToJoinJourneys() : goToJoinClassroomFirstTime()
      }
      dataTestId="start-join-classrooms-button"
      disabled={isUpdatePeriod}
    >
      {t('common.start')}
    </Button>
  );

  useEffect(() => {
    if (!sortedClassrooms) return;

    const getClassroomsPerRow = (width: number): number => {
      if (width > parseInt(theme.breakpoints.md)) {
        return 3;
      }

      if (width > parseInt(theme.breakpoints.sm)) {
        return 2;
      }

      return 1;
    };

    const calculateRows = (
      classroomsCount: number,
      classroomsPerRow: number
    ): number => {
      return Math.ceil(classroomsCount / classroomsPerRow);
    };

    const calculateMaxHeight = (isExpanded: boolean, rows: number): number => {
      const rowHeight = 126;
      const gap = 16;
      const expandedHeight = rows * rowHeight + gap * rows;
      const collapsedHeight = 2 * rowHeight + 32;
      const margin = isExtendable ? 6 : 16; // to don't cut shadows
      return (isExpanded ? expandedHeight : collapsedHeight) + margin;
    };

    const screenWidth =
      typeof window !== 'undefined' ? window.innerWidth : 1080;
    const classroomsPerRow = getClassroomsPerRow(screenWidth);
    const rows = calculateRows(sortedClassrooms.length, classroomsPerRow);
    const newMaxHeight = calculateMaxHeight(isExpanded, rows);

    setMaxHeight(newMaxHeight);
  }, [isExpanded, classrooms, theme, isExtendable]);

  if (!isSuccess && !isError) {
    return <MyClassesSkeleton />;
  }

  const isDropdownDisabled = noClasses || isUpdatePeriod;

  return (
    <Container>
      <MyClassesHeader
        isDisabled={isDropdownDisabled}
        onJoinClassroom={goToJoinClassroom}
        onJoinJourney={goToJoinJourneys}
        onError={handleOnError}
      />

      <UpdatePeriodAnnouncementPanel />

      {isError ? (
        <AnnouncementPanel
          text={t('classrooms.request.error')}
          type="error"
          canClose={false}
        />
      ) : (
        <ClassesContainer isExtendable={isExtendable} data-testid="my-classes">
          {noClasses ? (
            <NoClassesEmptyState
              goToJoinClassroomsButton={goToJoinClassroomsButton}
              classroomType={classroomType}
            />
          ) : (
            <ClassesWrapper maxHeight={maxHeight} noClasses={noClasses}>
              <ClassesGrid>
                {sortedClassrooms?.map((classroom, index) => (
                  <MyClassesClassCard
                    openEditModal={openEditModal}
                    onLeaveClassWithConfirmation={
                      handleLeaveClassWithConfirmation
                    }
                    onDeleteClassWithConfirmation={
                      handleDeleteClassWithConfirmation
                    }
                    key={`${classroom.name}-${index}`}
                    classroom={classroom}
                    index={index}
                    onClick={() => {
                      onSaveTeacherAccess(classroom.id!);
                      goToClassroom(classroom.id!);
                    }}
                    onOpenOptions={() =>
                      setEventData('launcher_open_class_options', {
                        assign_type: classroom.type,
                      })
                    }
                  />
                ))}
              </ClassesGrid>
            </ClassesWrapper>
          )}
          {isExtendable && (
            <Button
              size="S"
              variant="tertiary"
              rightIcon={isExpanded ? 'ExpandLessIcon' : 'ExpandMoreIcon'}
              onClick={() => setIsExpanded(!isExpanded)}
              dataTestId="show-more-classes-button"
              noRipple
            >
              {isExpanded
                ? t('my-classes.show-less')
                : t('my-classes.show-more')}
            </Button>
          )}
        </ClassesContainer>
      )}

      <ConfirmationModal {...confirmModalData} />
      <AddClassroomModal {...editModalData} />
    </Container>
  );
}
